/*!
 * TLS geo notice v1.2.1 - assets/tls-geo.js
 * Source: C:/Users/venek/Documents/upcart-vervanger/geo/theme/assets/tls-geo.js (installed by geo/install_geo.py).
 * Same file on NL, DE and COM. The store and the current page come from snippets/tls-geo.liquid.
 *
 * What it does: when the visitor's country belongs to ANOTHER of our stores (and that store sells + ships there),
 * it shows a small notice with two equal choices: go to the same page on that store, or stay. It NEVER redirects
 * by country (EU Geo-blocking Regulation 2018/302 art. 3, Google guidance), never asks for browser location,
 * shows nothing to crawlers, and builds every text with textContent (never as HTML).
 * Placement: a small card at the BOTTOM (phone: full width, above the sticky add-to-cart bar and the cookie button;
 * computer: bottom-left), so it never covers the header, the menu or the cart icon.
 * Esc inside the notice = 'stay' (30 days). Esc elsewhere on the page only counts when nothing else can own it (focus on
 * the page itself, no menu / search / zoom / popup / drawer open), and then only closes it for this visit ('later').
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module && module.exports) {
    module.exports = api;
  } else if (root && root.document) {
    try { api.boot(root); } catch (e) { /* never break the page */ }
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  // ================================ CONFIG ================================
  // The ONE place to change routing, countries, texts or the crawler list. After a change: run the tests
  // (node --test geo/tests/tls-geo.test.js) and install again (py -3 geo/install_geo.py ...). See geo/README.md.
  var CONFIG = {
    version: '1.2.1',
    // storefront domain of each store (the 'go' link always points here)
    domains: { nl: 'www.thelightsupplier.nl', de: 'www.thelightsupplier.de', com: 'thelightsupplier.com' },
    // language of each store = language of the notice that sends visitors TO that store
    lang: { nl: 'nl', de: 'de', com: 'en' },
    // the store a country belongs to; every country that is not listed belongs to `fallback`
    routes: { NL: 'nl', BE: 'nl', DE: 'de', AT: 'de', CH: 'de' },
    fallback: 'com',
    // countries each store really sells AND ships to. A store is only suggested for a country in its own list.
    // If the routed store does not serve the country, `fallback` is tried; if that does not serve it either,
    // nothing is shown. Remove a country here to stop suggesting that store for it (e.g. 'GB' from com).
    // NL -> BE and DE -> AT + CH: checked with a real cart test on 19 Oct 2026.
    serves: {
      nl: ['NL', 'BE'],
      de: ['DE', 'AT', 'CH'],
      com: ['AU', 'CA', 'DK', 'EE', 'FR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MC', 'NO', 'PL', 'PT', 'ES', 'TR', 'UA',
        'GB', 'US', 'NZ', 'UM', 'NL']
    },
    rememberDays: 30,
    // false: the visible line "we remember your choice for 30 days" is enough to keep the choice 30 days
    // (WP29 opinion 04/2012 par. 3.6). true: keep it 30 days only when Shopify's customer-privacy API reports
    // preferences consent; otherwise only for this browser session.
    requirePreferencesConsent: false,
    detectUrl: '/browsing_context_suggestions.json?country%5Benabled%5D=true',
    timeoutMs: 3000,       // country lookup timeout (no answer = unknown = no notice)
    delayMs: 1000,         // wait after the page 'load' event before looking up the country
    overlayWaitMs: 20000,  // BEFORE showing: wait at most this long for a cookie banner / popup / open cart drawer
    reshowWaitMs: 120000,  // a banner or popup opened while the notice was visible: the notice steps aside and comes
                           // back ONCE when it closes (waits at most this long). A second one: gone for this session.
    watchMs: 500,          // while visible: how often to look for a new popup / the sticky bar
    goParam: 'tls_geo',           // added to the 'go' link (?tls_geo=1), removed again on arrival
    testParam: 'tls_geo_country', // ?tls_geo_country=US forces a country (only on a NON-live theme)
    // cookie / consent banners: the notice waits (and steps aside) while one of these is visible
    // (.pd-cp-ui in .pd-cp-ui__showing = Pandectes' 'manage preferences' panel, which sits outside #pandectes-banner)
    consentSelectors: ['#pandectes-banner', '.cc-window', '.cc-banner', '#shopify-pc__banner', '.pd-cp-ui',
      '.pd-cp-ui__showing'],
    // Klaviyo popups / flyouts (and the 'klaviyoForms' open/close events): same as a cookie banner
    popupSelectors: ['.kl-private-reset-css-Xuajs1[role="dialog"]', 'div[role="dialog"][aria-label="POPUP Form"]',
      'div[role="dialog"][aria-label="FLYOUT Form"]', 'div[role="dialog"][aria-label="FULLSCREEN Form"]'],
    // things that close with Esc themselves: while one of these is visible, Esc is THEIRS and never closes the notice.
    // The theme's menu / filter drawers, its search pop-down + underlay, the desktop mega menu, the language/currency
    // pop-out, the product image zoom (PhotoSwipe) and modals (MicroModal), plus any open dialog of any app.
    escOwners: ['.drawer--visible', '.drawer--visible .drawer__content', '.underlay--visible', '#search-popdown.is-visible',
      '[data-hover-disclosure].is-visible', '.popout-list--visible', '.pswp--open', '.modal.is-open', '[aria-modal="true"]',
      '[role="dialog"]', 'dialog[open]'],
    // fixed things at the bottom of the screen the notice sits ABOVE: the sticky add-to-cart bar (only while it is
    // shown), the cookie-settings button and Shopify's preview bar. Heights are read live, never hard-coded.
    bottomBars: ['#tls-sticky-atc.is-visible', '.cc-revoke', '#PBarNextFrameWrapper', '#preview-bar-iframe'],
    // bars that SLIDE in with a transform (the sticky bar: translateY(110%) -> 0 in 0.25 s): their final place counts
    // from the first moment, so the card moves up together with the bar instead of 0.3 s later
    slideBars: ['#tls-sticky-atc.is-visible'],
    slideMs: 300,          // a bar slides AWAY: the card waits this long before it drops (so it never slides into it)
    // crawlers, link previews, SEO / AI bots, speed tests, monitoring and headless browsers: no request, no notice.
    // NOT bots: in-app browsers of the apps our ads run in (Pinterest/iOS, [Pinterest/Android], FBAN/FBAV, Instagram,
    // TikTok, Snapchat) and phones whose name ends in 'bot' (Cubot). So: names with word boundaries, never a bare
    // 'bot' / 'pinterest' / 'facebook' substring.
    bots: new RegExp([
      '\\b(?:googlebot|adsbot-google|mediapartners-google|google-inspectiontool|storebot-google|googleother|' +
        'google-cloudvertexbot|google-safety|apis-google|feedfetcher-google|google-read-aloud|' +
        'google-site-verification|googleproducer|google-cws|google-pagerenderer|google web preview|' +
        'google-structured-data-testing-tool|google-adwords-instant|google-agent|google favicon|googleimageproxy|' +
        'google-shopping-quality|google-merchant-center)\\b',
      '\\b(?:bingbot|adidxbot|bingpreview|microsoftpreview|msnbot(?:-media)?|duckduckbot(?:-https)?|duckassistbot|' +
        'yandex\\w*bot|baiduspider(?:-\\w+)?|seznambot|sogou (?:web|inst) spider|qwantify|qwantbot|petalbot|' +
        'applebot(?:-extended)?)\\b',
      '\\b(?:facebookexternalhit|facebookcatalog|meta-externalagent|meta-externalads|meta-externalfetcher|' +
        'meta-webindexer|pinterestbot|twitterbot|linkedinbot|slackbot(?:-linkexpanding)?|slack-imgproxy|' +
        'discordbot|telegrambot|skypeuripreview|embedly|redditbot|vkshare|tiktokspider)\\b',
      '^whatsapp\\/',
      '\\b(?:ahrefsbot|ahrefssiteaudit|semrushbot|siteauditbot|mj12bot|dotbot|rogerbot|screaming frog|sitebulb|' +
        'bytespider|gptbot|oai-searchbot|chatgpt-user|claudebot|claude-user|claude-searchbot|anthropic-ai|' +
        'perplexitybot|perplexity-user|ccbot|amazonbot|dataforseobot|barkrowler|blexbot|serpstatbot)\\b',
      '\\b(?:chrome-lighthouse|lighthouse|page ?speed|gtmetrix|ptst|pingdom|uptimerobot|statuscake|headlesschrome|' +
        'phantomjs|prerender|rendertron|datadogsynthetics|checkly|cookiebot|shopify-theme-inspector)\\b',
      // generic: a crawler's contact URL '(+http...)', a separate word bot/crawler/spider, '<name>bot/<version>', or
      // a crawler name right after 'compatible;' ('(compatible; TikTokSpider; ...)')
      '\\+https?:\\/\\/', '\\b(?:bot|crawler|spider|crawl|slurp)\\b', '\\b\\w*(?:bot|crawler|spider)\\/\\d',
      'compatible; ?[\\w .-]*?(?:bot|spider|crawler)\\b'
    ].join('|'), 'i'),
    // Texts per language of the TARGET store. Placeholders: {country} (in / from), {country_acc}, {country_to}
    // (German 'nach Österreich' / 'in die Schweiz'), {domain}, {days}.
    // countries: a different text for one country (e.g. the store's own country: no "also delivers to").
    // nl: je-form. de: Sie-form, like the DE store's own texts (theme + product pages; 54x Sie, 0x du on 19 Oct).
    texts: {
      nl: {
        title: 'Woon je in {country}?',
        body: 'Onze Nederlandse winkel levert ook in {country}.',
        go: 'Naar {domain}',
        stay: 'Hier blijven',
        note: 'We onthouden je keuze {days} dagen.',
        close: 'Sluiten',
        countries: { NL: { body: 'Bestel dan gewoon in onze Nederlandse winkel.' } }
      },
      de: {
        title: 'Wohnen Sie in {country}?',
        body: 'Unser deutscher Shop liefert auch {country_to}.',
        go: 'Weiter zu {domain}',
        stay: 'Hier bleiben',
        note: 'Wir merken uns Ihre Auswahl {days} Tage lang.',
        close: 'Schließen',
        toCountry: 'nach {name}',
        countries: { DE: { body: 'Dann bestellen Sie am besten in unserem deutschen Shop.' } }
      },
      en: {
        title: 'Shopping from {country}?',
        body: 'Our international store delivers to {country}.',
        go: 'Go to {domain}',
        stay: 'Stay here',
        note: 'We\u2019ll remember your choice for {days} days.',
        close: 'Close'
      }
    },
    // Country names per notice language, for every country in `serves` (so old browsers without Intl.DisplayNames,
    // Safari < 14.1, still get a text, and every browser gets the SAME name: 'Türkiye', never 'Turkey' in one and
    // 'Türkiye' in another). A string is used as is; German needs {dat, acc, to} for countries with an article.
    // A country missing here falls back to Intl.DisplayNames; without that: no notice.
    names: {
      nl: {
        NL: 'Nederland', BE: 'België', DE: 'Duitsland', AT: 'Oostenrijk', CH: 'Zwitserland', AU: 'Australië',
        CA: 'Canada', DK: 'Denemarken', EE: 'Estland', FR: 'Frankrijk', IE: 'Ierland', IT: 'Italië', LV: 'Letland',
        LT: 'Litouwen', LU: 'Luxemburg', MC: 'Monaco', NO: 'Noorwegen', PL: 'Polen', PT: 'Portugal', ES: 'Spanje',
        TR: 'Turkije', UA: 'Oekraïne', GB: 'het Verenigd Koninkrijk', US: 'de Verenigde Staten', NZ: 'Nieuw-Zeeland',
        UM: 'de Kleine afgelegen eilanden van de Verenigde Staten'
      },
      de: {
        NL: { dat: 'den Niederlanden', acc: 'die Niederlande', to: 'in die Niederlande' }, BE: 'Belgien',
        DE: 'Deutschland', AT: 'Österreich',
        CH: { dat: 'der Schweiz', acc: 'die Schweiz', to: 'in die Schweiz' }, AU: 'Australien', CA: 'Kanada',
        DK: 'Dänemark', EE: 'Estland', FR: 'Frankreich', IE: 'Irland', IT: 'Italien', LV: 'Lettland', LT: 'Litauen',
        LU: 'Luxemburg', MC: 'Monaco', NO: 'Norwegen', PL: 'Polen', PT: 'Portugal', ES: 'Spanien',
        TR: { dat: 'der Türkei', acc: 'die Türkei', to: 'in die Türkei' },
        UA: { dat: 'der Ukraine', acc: 'die Ukraine', to: 'in die Ukraine' },
        GB: { dat: 'dem Vereinigten Königreich', acc: 'das Vereinigte Königreich', to: 'ins Vereinigte Königreich' },
        US: { dat: 'den USA', acc: 'die USA', to: 'in die USA' }, NZ: 'Neuseeland',
        UM: { dat: 'den Amerikanischen Überseeinseln', acc: 'die Amerikanischen Überseeinseln',
          to: 'auf die Amerikanischen Überseeinseln' }
      },
      en: {
        NL: 'the Netherlands', BE: 'Belgium', DE: 'Germany', AT: 'Austria', CH: 'Switzerland', AU: 'Australia',
        CA: 'Canada', DK: 'Denmark', EE: 'Estonia', FR: 'France', IE: 'Ireland', IT: 'Italy', LV: 'Latvia',
        LT: 'Lithuania', LU: 'Luxembourg', MC: 'Monaco', NO: 'Norway', PL: 'Poland', PT: 'Portugal', ES: 'Spain',
        TR: 'Türkiye', UA: 'Ukraine', GB: 'the UK', US: 'the US', NZ: 'New Zealand', UM: 'the U.S. Outlying Islands'
      }
    }
  };

  // storage keys (first-party, this domain only). Stored value: {"choice","store","ts"} - nothing else.
  var KEY = 'tls_geo_choice';      // localStorage: 'stay' / 'go' for rememberDays
  var SKEY = 'tls_geo_session';    // sessionStorage: this session's choice ('stay'/'go'/'later'/'arrived')
  var CKEY = 'tls_geo_cc';         // sessionStorage: detected country for this session ('-' = unknown)
  var TKEY = 'tls_geo_test_country'; // localStorage, set by hand in devtools: forced country (testing on live)
  var DAY = 86400000;
  var PATHS = { product: '/products/', collection: '/collections/', page: '/pages/' };

  // ============================ pure helpers ============================
  function has(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }

  function normCountry(x) {
    if (typeof x !== 'string') return null;
    var c = x.trim();
    return /^[A-Za-z]{2}$/.test(c) ? c.toUpperCase() : null;
  }

  function servesCountry(cfg, storeKey, cc) {
    var list = cfg.serves && cfg.serves[storeKey];
    return !!list && list.indexOf(cc) >= 0;
  }

  // the store to suggest, or null (unknown country, nobody serves it, or the visitor is already on it)
  function pickTarget(country, current, cfg) {
    cfg = cfg || CONFIG;
    var cc = normCountry(country);
    if (!cc || !has(cfg.domains, current)) return null;
    var routed = has(cfg.routes, cc) ? cfg.routes[cc] : cfg.fallback;
    var target = null;
    if (routed && servesCountry(cfg, routed, cc)) target = routed;
    else if (cfg.fallback && routed !== cfg.fallback && servesCountry(cfg, cfg.fallback, cc)) target = cfg.fallback;
    if (!target || target === current || !has(cfg.domains, target)) return null;
    return target;
  }

  function isBot(ua, webdriver, re) {
    if (webdriver === true) return true;
    if (typeof ua !== 'string' || !ua) return true;
    return (re || CONFIG.bots).test(ua);
  }

  // a stored choice that is still valid, or null. longTerm: only 'stay'/'go' count.
  function parseChoice(raw, now, days, longTerm) {
    if (typeof raw !== 'string' || !raw || raw.length > 200) return null;
    var o;
    try { o = JSON.parse(raw); } catch (e) { return null; }
    if (!o || typeof o !== 'object') return null;
    var allowed = longTerm ? ['stay', 'go'] : ['stay', 'go', 'later', 'arrived'];
    if (allowed.indexOf(o.choice) < 0) return null;
    if (typeof o.ts !== 'number' || !isFinite(o.ts)) return null;
    if (o.ts > now + DAY) return null;               // from the future (clock was changed): ignore
    if (now - o.ts > days * DAY) return null;        // expired
    return { choice: o.choice, store: typeof o.store === 'string' ? o.store : '', ts: o.ts };
  }

  function serializeChoice(choice, storeKey, now) {
    return JSON.stringify({ choice: choice, store: storeKey, ts: now });
  }

  function safeDecode(s) {
    try { return decodeURIComponent(s); } catch (e) { return s; }
  }

  // fallback only (the Liquid snippet normally gives type + handle): page type + handle from a URL path
  function pageFromPath(pathname) {
    var parts = String(pathname || '/').split('/').filter(Boolean).map(safeDecode);
    var i = parts.lastIndexOf('products');
    if (i >= 0 && parts[i + 1]) return { type: 'product', handle: parts[i + 1] };
    if (parts[0] === 'collections' && parts[1]) return { type: 'collection', handle: parts[1] };
    if (parts[0] === 'collections') return { type: 'list-collections', handle: '' };
    if (parts[0] === 'pages' && parts[1]) return { type: 'page', handle: parts[1] };
    if (parts[0] === 'blogs') return { type: parts.length > 2 ? 'article' : 'blog', handle: '' };
    if (parts[0] === 'policies') return { type: 'policy', handle: '' };
    if (!parts.length) return { type: 'index', handle: '' };
    return { type: 'other', handle: '' };
  }

  // path on the target store. map = this page's exceptions {"<store>": "<handle>" | null} (from tls-geo-map).
  // missing = the map says the target has no equivalent (link goes to its home page instead).
  function targetPath(page, map, target) {
    var type = page && page.type;
    var handle = page && page.handle;
    if (has(PATHS, type) && typeof handle === 'string' && handle) {
      var h = handle;
      if (map && typeof map === 'object' && has(map, target)) {
        h = map[target];
        if (typeof h !== 'string' || !h) return { path: '/', missing: true };
      }
      return { path: PATHS[type] + encodeURIComponent(h), missing: false };
    }
    if (type === 'list-collections') return { path: '/collections', missing: false };
    if (type === 'blog' || type === 'article') return { path: '/blogs/news', missing: false };
    if (type === 'policy' && page && /^\/policies\/[a-z0-9-]{1,60}$/.test(String(page.path || ''))) {
      return { path: page.path, missing: false };
    }
    return { path: '/', missing: false };
  }

  // the 'go' link: target domain + equivalent path + ?tls_geo=1. Nothing from the current query string.
  function buildTargetUrl(cfg, page, map, target) {
    cfg = cfg || CONFIG;
    if (!has(cfg.domains, target)) return null;
    var t = targetPath(page, map, target);
    var href = 'https://' + cfg.domains[target] + t.path + '?' + cfg.goParam + '=1';
    return isSafeHref(href, cfg) ? { href: href, missing: t.missing } : null;
  }

  function isSafeHref(href, cfg) {
    cfg = cfg || CONFIG;
    if (typeof href !== 'string' || /[\s"'<>\\`]/.test(href)) return false;
    for (var k in cfg.domains) {
      if (has(cfg.domains, k) && href.indexOf('https://' + cfg.domains[k] + '/') === 0) return true;
    }
    return false;
  }

  function getParam(search, name) {
    var q = String(search || '').replace(/^\?/, '');
    if (!q) return null;
    var parts = q.split('&');
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i]) continue;
      var eq = parts[i].indexOf('=');
      var k = safeDecode((eq < 0 ? parts[i] : parts[i].slice(0, eq)).replace(/\+/g, ' '));
      if (k === name) return eq < 0 ? '' : safeDecode(parts[i].slice(eq + 1).replace(/\+/g, ' '));
    }
    return null;
  }

  // '?a=1&tls_geo=1&b=2' minus ['tls_geo'] -> '?a=1&b=2' ('' when nothing is left)
  function removeParams(search, names) {
    var q = String(search || '').replace(/^\?/, '');
    if (!q) return '';
    var kept = q.split('&').filter(function (p) {
      if (!p) return false;
      var eq = p.indexOf('=');
      var k = safeDecode((eq < 0 ? p : p.slice(0, eq)).replace(/\+/g, ' '));
      return names.indexOf(k) < 0;
    });
    return kept.length ? '?' + kept.join('&') : '';
  }

  // test override: ?tls_geo_country=XX on a NON-live theme (preview), or the devtools localStorage key
  function forcedCountry(search, role, stored, cfg) {
    cfg = cfg || CONFIG;
    var v = getParam(search, cfg.testParam);
    if (v !== null && typeof role === 'string' && role && role !== 'main') return normCountry(v);
    return normCountry(stored);
  }

  // a visitor who followed the notice to a page that does not exist here: search for the handle's words
  function softLandingUrl(pathname) {
    var parts = String(pathname || '').split('/').filter(Boolean);
    if (!parts.length) return null;
    var last = safeDecode(parts[parts.length - 1]).replace(/\.(js|json|xml|atom|oembed)$/i, '');
    var words = last.replace(/[\u2122\u00ae\u00a9]/g, '')
      .replace(/[^0-9A-Za-z\u00c0-\u024f]+/g, ' ')
      .trim()
      .replace(/\s\d{1,2}$/, '')   // Shopify duplicate suffix: campify-string-2 -> campify string
      .trim()
      .slice(0, 80)
      .trim();
    if (!words) return null;
    return '/search?q=' + encodeURIComponent(words) + '&options%5Bprefix%5D=last';
  }

  function countryFromResponse(j) {
    try { return normCountry(j.detected_values.country.handle); } catch (e) { return null; }
  }

  function fill(tpl, vars) {
    return String(tpl).replace(/\{(\w+)\}/g, function (m, k) { return has(vars, k) ? String(vars[k]) : m; });
  }

  // country name in the notice language. slot: 'dat' = {country}, 'acc' = {country_acc}, 'to' = {country_to}.
  // Built-in table first (same text in every browser), then Intl.DisplayNames, else null (= no notice).
  // toTpl: how 'to' is made from a plain name (German 'nach {name}'); an object entry gives its own 'to'.
  function countryName(cc, lang, slot, table, intl, toTpl) {
    var o = table && has(table, cc) ? table[cc] : null;
    var base = null;
    if (o && typeof o === 'object') {
      if (typeof o[slot] === 'string' && o[slot]) return o[slot];
      base = o.dat || o.name || null;
    } else if (typeof o === 'string' && o) {
      base = o;
    }
    if (!base) {
      try {
        if (intl && intl.DisplayNames) {
          var n = new intl.DisplayNames([lang], { type: 'region' }).of(cc);
          if (typeof n === 'string' && n && n !== cc) base = n;
        }
      } catch (e) { /* fall through */ }
    }
    if (!base) return null;
    if (slot === 'to' && typeof toTpl === 'string' && toTpl) return fill(toTpl, { name: base });
    return base;
  }

  // all texts of the notice in the TARGET store's language, or null when something is missing
  function buildTexts(cfg, current, target, cc, intl) {
    cfg = cfg || CONFIG;
    var lang = cfg.lang[target];
    var t = lang && cfg.texts[lang];
    if (!t) return null;
    var table = cfg.names && cfg.names[lang];
    var dat = countryName(cc, lang, 'dat', table, intl);
    var acc = countryName(cc, lang, 'acc', table, intl);
    var to = countryName(cc, lang, 'to', table, intl, t.toCountry);
    if (!dat || !acc || !to) return null;
    var vars = {
      country: dat, country_acc: acc, country_to: to, days: cfg.rememberDays,
      domain: String(cfg.domains[target]).replace(/^www\./, '')
    };
    var own = t.countries && has(t.countries, cc) ? t.countries[cc] : null;
    var out = { lang: lang, target: target, current: current };
    var keys = ['title', 'body', 'go', 'stay', 'note', 'close'];
    for (var i = 0; i < keys.length; i++) {
      var tpl = own && typeof own[keys[i]] === 'string' ? own[keys[i]] : t[keys[i]];
      if (typeof tpl !== 'string') return null;
      out[keys[i]] = fill(tpl, vars);
      if (/\{\w+\}/.test(out[keys[i]])) return null;
    }
    return out;
  }

  // the notice as DOM nodes; every text goes in via textContent, the link via a checked href
  function buildNotice(doc, t, href) {
    if (!isSafeHref(href)) return null;
    function el(tag, cls) { var e = doc.createElement(tag); if (cls) e.className = cls; return e; }
    var root = el('section', 'tlsg');
    root.id = 'tls-geo';
    root.setAttribute('role', 'region');
    root.setAttribute('aria-labelledby', 'tls-geo-title');
    root.setAttribute('lang', t.lang);
    root.setAttribute('data-nosnippet', '');
    var msg = el('div', 'tlsg__msg');
    msg.setAttribute('role', 'status');
    msg.setAttribute('aria-live', 'polite');
    msg.setAttribute('aria-atomic', 'true');
    var title = el('p', 'tlsg__title');
    title.id = 'tls-geo-title';
    var body = el('p', 'tlsg__text');
    msg.appendChild(title);
    msg.appendChild(body);
    var actions = el('div', 'tlsg__actions');
    var go = el('a', 'tlsg__btn tlsg__go');
    go.setAttribute('href', href);
    go.textContent = t.go;
    var stay = el('button', 'tlsg__btn tlsg__stay');
    stay.setAttribute('type', 'button');
    stay.textContent = t.stay;
    actions.appendChild(go);
    actions.appendChild(stay);
    var note = el('p', 'tlsg__note');
    note.textContent = t.note;
    var close = el('button', 'tlsg__close');
    close.setAttribute('type', 'button');
    close.setAttribute('aria-label', t.close);
    close.setAttribute('title', t.close);
    root.appendChild(msg);
    root.appendChild(actions);
    root.appendChild(note);
    root.appendChild(close);
    return {
      root: root, msg: msg, title: title, body: body, go: go, stay: stay, note: note, close: close,
      // filled a moment after the live region is in the page, so screen readers announce it
      fill: function () { title.textContent = t.title; body.textContent = t.body; }
    };
  }

  // the notice goes EARLY in the page (right after the theme's 'skip to content' link), so keyboard users reach it
  // within a few Tab presses. It is position: fixed, so this moves nothing on the page.
  function insertEarly(doc, node) {
    var body = doc.body;
    var skip = null;
    try { skip = doc.querySelector('.skip-link'); } catch (e) { skip = null; }
    if (skip && skip.parentNode === body) body.insertBefore(node, skip.nextSibling);
    else if (body.firstChild) body.insertBefore(node, body.firstChild);
    else body.appendChild(node);
  }

  function plainConfig() {
    var c = JSON.parse(JSON.stringify(CONFIG));
    c.bots = CONFIG.bots.source;
    return c;
  }

  // ============================ browser part ============================
  function storage(win, kind) {
    try {
      var s = win[kind];
      s.setItem('__tlsg', '1');
      s.removeItem('__tlsg');
      return s;
    } catch (e) { return null; }
  }
  function sget(s, k) { try { return s ? s.getItem(k) : null; } catch (e) { return null; } }
  function sset(s, k, v) { try { if (s) { s.setItem(k, v); return true; } } catch (e) { /* blocked */ } return false; }

  // the element's own vertical translate right now (e.g. halfway through a slide-in transition), 0 when none
  function ownShiftY(win, el) {
    try {
      var t = win.getComputedStyle ? win.getComputedStyle(el).transform : '';
      var m = /^matrix(3d)?\(([^)]*)\)$/.exec(String(t || ''));
      if (!m) return 0;
      var v = m[2].split(',');
      var ty = parseFloat(v[m[1] ? 13 : 5]);
      return isFinite(ty) ? ty : 0;
    } catch (e) { return 0; }
  }

  // the element's box; settled = without its own translate, i.e. where a sliding bar ends up
  function boxOf(win, el, settled) {
    var r = el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    if (!r || !settled) return r;
    var dy = ownShiftY(win, el);
    if (!dy) return r;
    return { top: r.top - dy, bottom: r.bottom - dy, left: r.left, right: r.right, width: r.width, height: r.height };
  }

  function isShown(win, el, settled) {
    if (!el || (el.classList && el.classList.contains('cc-invisible'))) return false;
    var r = boxOf(win, el, settled);
    if (!r || r.width < 2 || r.height < 2) return false;
    var vh = win.innerHeight || 0, vw = win.innerWidth || 0;
    if (r.bottom <= 0 || r.right <= 0 || (vh && r.top >= vh) || (vw && r.left >= vw)) return false;
    var cs = win.getComputedStyle ? win.getComputedStyle(el) : null;
    if (cs && (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05)) return false;
    return true;
  }

  function anyShown(win, selectors) {
    var doc = win.document;
    for (var i = 0; i < selectors.length; i++) {
      var list = [];
      try { list = doc.querySelectorAll(selectors[i]); } catch (e) { list = []; }
      for (var j = 0; j < list.length; j++) if (isShown(win, list[j])) return true;
    }
    return false;
  }

  // what is open right now: the cart drawer (html.tlsc-lock), a cookie banner, a (Klaviyo) popup
  function overlayState(win, klaviyoOpen) {
    var html = win.document.documentElement;
    return {
      drawer: !!(html && html.classList && html.classList.contains('tlsc-lock')),
      consent: anyShown(win, CONFIG.consentSelectors),
      popup: !!klaviyoOpen || anyShown(win, CONFIG.popupSelectors)
    };
  }

  function overlayOpen(win, klaviyoOpen) {
    var s = overlayState(win, klaviyoOpen);
    return s.drawer || s.consent || s.popup;
  }

  // px taken at the bottom of the screen by the sticky add-to-cart bar / cookie button / preview bar (the highest
  // one counts: they all sit at the bottom). Only elements that really are at the bottom and visible.
  // A bar in `slides` counts with its FINAL place (its own translate ignored), also while it is still sliding in.
  function bottomOffset(win, selectors, slides) {
    var vh = win.innerHeight || 0;
    if (!vh) return 0;
    var doc = win.document, max = 0;
    selectors = selectors || CONFIG.bottomBars;
    slides = slides || CONFIG.slideBars || [];
    for (var i = 0; i < selectors.length; i++) {
      var list = [];
      var settled = slides.indexOf(selectors[i]) >= 0;
      try { list = doc.querySelectorAll(selectors[i]); } catch (e) { list = []; }
      for (var j = 0; j < list.length; j++) {
        if (!isShown(win, list[j], settled)) continue;
        var r = boxOf(win, list[j], settled);
        if (r.top < vh / 2 || r.bottom < vh - 80) continue;   // not a bar at the bottom (e.g. a full-height drawer)
        max = Math.max(max, Math.ceil(vh - r.top));
      }
    }
    return Math.min(max, Math.round(vh / 2));
  }

  function boot(win) {
    var doc = win.document;
    if (!doc || win.TLSGeo) return;
    var state = { version: CONFIG.version, store: null, page: null, country: null, source: null,
      target: null, href: null, status: 'init', pauses: 0 };
    var api = {};
    Object.defineProperty(api, 'version', { value: CONFIG.version, enumerable: true });
    Object.defineProperty(api, 'state', { enumerable: true, get: function () { return JSON.parse(JSON.stringify(state)); } });
    Object.defineProperty(api, 'config', { enumerable: true, get: plainConfig });
    try { win.TLSGeo = Object.freeze(api); } catch (e) { /* ignore */ }
    function set(status) { state.status = status; }

    var cfgEl = doc.getElementById('tls-geo-cfg');
    var cfg = null;
    try { cfg = JSON.parse(cfgEl ? cfgEl.textContent : ''); } catch (e) { cfg = null; }
    if (!cfg || typeof cfg !== 'object' || !has(CONFIG.domains, cfg.store)) return set('no-config');
    state.store = cfg.store;
    var loc = win.location;
    var path = String(loc.pathname || '/');
    var page = cfg.type
      ? { type: String(cfg.type), handle: typeof cfg.handle === 'string' ? cfg.handle : '' }
      : pageFromPath(path);
    page.path = path;
    state.page = { type: page.type, handle: page.handle };
    var map = cfg.map && typeof cfg.map === 'object' ? cfg.map : {};

    // crawlers, theme editor, iframes: nothing at all (no request, no notice, no redirect)
    var nav = win.navigator || {};
    if (isBot(nav.userAgent, nav.webdriver)) return set('bot');
    if (win.Shopify && win.Shopify.designMode) return set('editor');
    try { if (win.self !== win.top) return set('iframe'); } catch (e) { return set('iframe'); }

    var ls = storage(win, 'localStorage');
    var ss = storage(win, 'sessionStorage');
    var search = String(loc.search || '');
    var now = function () { return new Date().getTime(); };

    // arrival from another of our stores through the notice
    if (getParam(search, CONFIG.goParam) === '1') {
      if (page.type === '404') {
        set('arrival-404');
        try { loc.replace(softLandingUrl(path) || '/'); } catch (e) { /* ignore */ }
        return;
      }
      sset(ss, SKEY, serializeChoice('arrived', cfg.store, now()));
      try {
        if (win.history && win.history.replaceState) {
          win.history.replaceState(win.history.state, '', path + removeParams(search, [CONFIG.goParam]) + (loc.hash || ''));
        }
      } catch (e) { /* ignore */ }
      return set('arrived');
    }

    var role = win.Shopify && win.Shopify.theme ? win.Shopify.theme.role : null;
    var forced = forcedCountry(search, role, sget(ls, TKEY));
    // browser storage blocked (cookies blocked, some in-app WebViews): a choice could not be remembered, although the
    // notice says it is, and it would come back (with a new country lookup) on every page. Show nothing, ask nothing.
    // The test override (?tls_geo_country= on a preview theme) still works.
    if (!forced && (!ls || !ss)) return set('no-storage');
    if (!forced) {
      var kept = parseChoice(sget(ls, KEY), now(), CONFIG.rememberDays, true) ||
        parseChoice(sget(ss, SKEY), now(), CONFIG.rememberDays, false);
      if (kept) return set('remembered-' + kept.choice);
    }

    // Klaviyo tells when its popup / flyout opens and closes; listened to from the start, so a popup that opened
    // before the notice is known too
    var kl = { open: false, onChange: null };
    win.addEventListener('klaviyoForms', function (e) {
      var type = e && e.detail && e.detail.type;
      if (type === 'open') kl.open = true;
      else if (type === 'close') kl.open = false;
      else return;
      if (kl.onChange) kl.onChange();
    });

    function remember(choice, storeKey) {
      var v = serializeChoice(choice, storeKey, now());
      sset(ss, SKEY, v);
      if (forced || (choice !== 'stay' && choice !== 'go')) return;
      if (CONFIG.requirePreferencesConsent) {
        var ok = false;
        try {
          var cp = win.Shopify && win.Shopify.customerPrivacy;
          ok = !!(cp && typeof cp.preferencesProcessingAllowed === 'function' && cp.preferencesProcessingAllowed() === true);
        } catch (e) { ok = false; }
        if (!ok) return;
      }
      sset(ls, KEY, v);
    }

    function detect(cb) {
      var cached = sget(ss, CKEY);
      if (cached === '-') return cb(null, 'session-unknown');
      if (normCountry(cached)) return cb(normCountry(cached), 'session');
      if (typeof win.fetch !== 'function') return cb(null, 'no-fetch');
      var ctrl = typeof win.AbortController === 'function' ? new win.AbortController() : null;
      var done = false;
      var timer = win.setTimeout(function () {
        try { if (ctrl) ctrl.abort(); } catch (e) { /* ignore */ }
        finish(null, 'timeout');
      }, CONFIG.timeoutMs);
      function finish(cc, why) {
        if (done) return;
        done = true;
        win.clearTimeout(timer);
        sset(ss, CKEY, cc || '-');   // one lookup per session; an error is never retried in this session
        cb(cc, why);
      }
      var opts = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
      if (ctrl) opts.signal = ctrl.signal;
      try {
        win.fetch(CONFIG.detectUrl, opts).then(function (r) {
          if (!r || r.status !== 200) throw new Error('status');
          return r.json();
        }).then(function (j) {
          var cc = countryFromResponse(j);
          finish(cc, cc ? 'lookup' : 'lookup-empty');
        }, function () { finish(null, 'lookup-failed'); });
      } catch (e) { finish(null, 'lookup-failed'); }
    }

    function whenClear(cb) {
      var waited = 0;
      (function check() {
        if (!overlayOpen(win, kl.open)) return cb(true);
        if (waited >= CONFIG.overlayWaitMs) return cb(false);
        waited += CONFIG.watchMs;
        win.setTimeout(check, CONFIG.watchMs);
      })();
    }

    function loadCss(cb) {
      var href = cfg.css;
      if (typeof href !== 'string' || !(/^(https:)?\/\/[^/\s"'<>]+\/[^\s"'<>]*$/.test(href) || /^\/[^/\s"'<>][^\s"'<>]*$/.test(href))) {
        return cb(false);
      }
      var link = doc.createElement('link');
      var done = false;
      var finish = function (ok) { if (!done) { done = true; cb(ok); } };
      link.id = 'tls-geo-css';
      link.rel = 'stylesheet';
      link.onload = function () { finish(true); };
      link.onerror = function () { finish(false); };
      link.setAttribute('href', href);
      win.setTimeout(function () { finish(false); }, 8000);
      (doc.head || doc.documentElement).appendChild(link);
    }

    function show(t, href, target) {
      loadCss(function (ok) {
        if (!ok) return set('css-failed');
        // page replaced (e.g. an error page) or already shown: do nothing
        if (!doc.getElementById('tls-geo-cfg') || !doc.body || doc.getElementById('tls-geo')) return set('page-changed');
        var n = buildNotice(doc, t, href);
        if (!n) return set('bad-link');
        var html = doc.documentElement;
        var pad = null;          // scroll-padding we set (so focused elements are never hidden behind the notice)
        var closed = false;
        var paused = false;      // stepped aside for a banner / popup that opened later
        var pausedAt = 0;
        var watch = null;
        var bottom = -1;
        var downTimer = null;    // a bar slides away: the card drops a moment later
        var observer = null;
        var reduce = false;
        try { reduce = !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { reduce = false; }

        function focusMain() {
          var m = doc.getElementById('MainContent') || doc.querySelector('main');
          if (!m) return;
          try {
            if (!m.hasAttribute('tabindex')) m.setAttribute('tabindex', '-1');
            m.focus({ preventScroll: true });
          } catch (e) { /* ignore */ }
        }
        function clearPad() {
          if (pad) html.style[pad.side] = pad.value;
          pad = null;
        }
        function setPad() {
          if (closed || paused || !n.root.getBoundingClientRect) return;
          var r = n.root.getBoundingClientRect();
          var vh = win.innerHeight || 0;
          if (!vh || !r.height) return;
          var top = r.top < vh / 2;
          var side = top ? 'scrollPaddingTop' : 'scrollPaddingBottom';
          if (pad && pad.side !== side) clearPad();
          if (!pad) pad = { side: side, value: html.style[side] || '' };
          html.style[side] = Math.ceil(top ? r.bottom + 8 : vh - r.top + 8) + 'px';
        }
        function cancelDown() {
          if (downTimer) win.clearTimeout(downTimer);
          downTimer = null;
        }
        function applyBottom(b) {
          bottom = b;
          try { n.root.style.setProperty('--tlsg-bottom', b + 'px'); } catch (e) { /* very old browser */ }
          win.setTimeout(setPad, reduce ? 0 : 320);
        }
        // keep the card above the sticky add-to-cart bar / cookie button (their height is read, never assumed).
        // Up: at once (together with a bar that slides in). Down: only after the bar has slid away.
        function place() {
          if (closed || paused) return;
          var b = bottomOffset(win);
          if (b === bottom) { cancelDown(); return; }
          if (bottom >= 0 && b < bottom) {
            if (!downTimer) {
              downTimer = win.setTimeout(function () {
                downTimer = null;
                if (closed || paused) return;
                var now2 = bottomOffset(win);
                if (now2 !== bottom) applyBottom(now2);
              }, CONFIG.slideMs);
            }
            return;
          }
          cancelDown();
          applyBottom(b);
        }
        function onResize() {
          place();
          win.setTimeout(setPad, reduce ? 0 : 320);   // the card can change size (phone <-> computer layout)
        }
        function stopWatch() {
          if (watch) win.clearTimeout(watch);
          watch = null;
        }
        function hide() {
          if (closed) return;
          closed = true;
          kl.onChange = null;
          stopWatch();
          cancelDown();
          var hadFocus = false;
          try { hadFocus = n.root.contains(doc.activeElement); } catch (e) { hadFocus = false; }
          clearPad();
          win.removeEventListener('keydown', onKey, true);
          win.removeEventListener('resize', onResize);
          try { if (observer) observer.disconnect(); } catch (e) { /* ignore */ }
          n.root.classList.remove('is-visible');
          var remove = function () { if (n.root.parentNode) n.root.parentNode.removeChild(n.root); };
          if (reduce || paused) remove(); else win.setTimeout(remove, 300);
          if (hadFocus) focusMain();
        }
        function decide(choice, storeKey) {
          if (closed) return;
          remember(choice, storeKey);
          set('chose-' + choice);
          hide();
        }
        function giveUp() {
          // a second banner / popup, or the first one stayed open too long: not again in this session
          if (closed) return;
          remember('later', cfg.store);
          set('later-overlay');
          hide();
        }
        function pause() {
          if (state.pauses >= 1) return giveUp();
          state.pauses += 1;
          paused = true;
          pausedAt = now();
          var hadFocus = false;
          try { hadFocus = n.root.contains(doc.activeElement); } catch (e) { hadFocus = false; }
          clearPad();
          n.root.classList.remove('is-visible');
          n.root.classList.add('is-paused');
          set('paused');
          if (hadFocus) focusMain();
        }
        function resume() {
          paused = false;
          bottom = -1;
          n.root.classList.remove('is-paused');
          place();
          win.setTimeout(function () {
            if (closed || paused) return;
            n.root.classList.add('is-visible');
            win.setTimeout(setPad, reduce ? 0 : 320);
          }, 30);
          set('shown');
        }
        // while the notice is on screen (or stepped aside): a banner / popup that opens later, the sticky bar
        function check() {
          if (closed) return;
          var s = overlayState(win, kl.open);
          var blocked = s.consent || s.popup;   // the open cart drawer is handled by CSS (hidden while open)
          if (!paused && blocked) pause();
          else if (paused && !blocked) resume();
          else if (paused && now() - pausedAt > CONFIG.reshowWaitMs) giveUp();
          else place();
        }
        function tick() {
          watch = null;
          check();
          if (!closed) watch = win.setTimeout(tick, CONFIG.watchMs);
        }
        // Esc elsewhere on the page: only when nothing else can own it, and then only for this visit ('later').
        // Not when the focus is in a field, a menu, a button or anything else on the page (the theme closes its search,
        // menu, zoom and drop-downs on Esc), not while a theme overlay / cart drawer / banner / popup is open, and not
        // when someone else already handled the key. Runs in the capture phase: the overlays are still open then.
        // Esc INSIDE the notice is handled by its own listener below (= 'stay', 30 days).
        function onKey(e) {
          if (!e || (e.key !== 'Escape' && e.key !== 'Esc') || closed || paused) return;
          if (e.defaultPrevented || e.isComposing) return;
          var a = doc.activeElement;
          if (a && n.root.contains(a)) return;
          if (a && a !== doc.body && a !== doc.documentElement) return;
          var s = overlayState(win, kl.open);
          if (s.drawer || s.consent || s.popup || anyShown(win, CONFIG.escOwners)) return;
          decide('later', cfg.store);
        }

        // clicks inside the notice must not reach page-wide listeners (Pandectes closes on any window click)
        n.root.addEventListener('click', function (e) { e.stopPropagation(); });
        n.root.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' || e.key === 'Esc') { e.stopPropagation(); decide('stay', cfg.store); }
        });
        n.stay.addEventListener('click', function () { decide('stay', cfg.store); });
        n.close.addEventListener('click', function () { decide('stay', cfg.store); });
        n.go.addEventListener('click', function (e) {
          remember('go', target);
          set('chose-go');
          if (e && (e.ctrlKey || e.metaKey || e.shiftKey)) hide();   // opened in a new tab/window
        });
        win.addEventListener('keydown', onKey, true);
        win.addEventListener('resize', onResize);
        kl.onChange = function () { stopWatch(); tick(); };
        try {
          var bar = doc.getElementById('tls-sticky-atc');
          if (bar && typeof win.MutationObserver === 'function') {
            observer = new win.MutationObserver(function () { place(); win.setTimeout(place, 320); });
            observer.observe(bar, { attributes: true, attributeFilter: ['class'] });
          }
        } catch (e) { observer = null; }

        insertEarly(doc, n.root);
        set('shown');
        place();
        win.setTimeout(function () {
          if (closed) return;
          n.fill();
          if (!paused) n.root.classList.add('is-visible');
          win.setTimeout(setPad, reduce ? 0 : 320);
          stopWatch();   // a Klaviyo event in these 60 ms may already have started the watch: never two loops
          tick();
        }, 60);
      });
    }

    function run() {
      set('detecting');
      var got = function (cc, source) {
        state.country = cc;
        state.source = source;
        if (!cc) return set('unknown');
        var target = pickTarget(cc, cfg.store, CONFIG);
        if (!target) return set('no-target');
        var link = buildTargetUrl(CONFIG, page, map, target);
        if (!link) return set('no-link');
        // no equivalent on the other store, and this store itself delivers here: let them buy it here
        if (link.missing && servesCountry(CONFIG, cfg.store, cc)) return set('no-equivalent');
        var t = buildTexts(CONFIG, cfg.store, target, cc, win.Intl);
        if (!t) return set('no-text');
        state.target = target;
        state.href = link.href;
        set('waiting');
        whenClear(function (clear) {
          if (!clear) return set('overlay-open');
          show(t, link.href, target);
        });
      };
      if (forced) return got(forced, 'test');
      detect(got);
    }

    function afterLoad(cb) {
      var later = function () {
        win.setTimeout(function () {
          if (typeof win.requestIdleCallback === 'function') win.requestIdleCallback(function () { cb(); }, { timeout: 1500 });
          else cb();
        }, CONFIG.delayMs);
      };
      if (doc.readyState === 'complete') later();
      else win.addEventListener('load', later);
    }

    set('scheduled');
    if (doc.prerendering) {
      doc.addEventListener('prerenderingchange', function () { afterLoad(run); });
    } else {
      afterLoad(run);
    }
  }

  return {
    CONFIG: CONFIG,
    KEYS: { choice: KEY, session: SKEY, country: CKEY, test: TKEY },
    normCountry: normCountry,
    servesCountry: servesCountry,
    pickTarget: pickTarget,
    isBot: isBot,
    parseChoice: parseChoice,
    serializeChoice: serializeChoice,
    pageFromPath: pageFromPath,
    targetPath: targetPath,
    buildTargetUrl: buildTargetUrl,
    isSafeHref: isSafeHref,
    getParam: getParam,
    removeParams: removeParams,
    forcedCountry: forcedCountry,
    softLandingUrl: softLandingUrl,
    countryFromResponse: countryFromResponse,
    countryName: countryName,
    buildTexts: buildTexts,
    buildNotice: buildNotice,
    insertEarly: insertEarly,
    overlayState: overlayState,
    overlayOpen: overlayOpen,
    bottomOffset: bottomOffset,
    boot: boot
  };
});
